import { useCallback, useEffect, useMemo, useState } from 'react';
import { api, type AcctCorp, type CashOutHow, type CashOutMatchResult } from '../lib/api';
import { ErrorBox, dash, money } from '../components/DataTable';
import { FitBox } from '../components/FitBox';
import { hitAny, searchWords } from '../lib/search';

/**
 * 회계 › 출금 대조 — 은행 출금 ↔ 출납일보(2026-11-02 회의 M8 · `plan/accounting.md` 5-L 2단계). 서버 `functions/src/cashOutMatch.ts`.
 * 읽기만 한다. 「은행에만」(출납 기록 없는 출금)이 이 화면의 핵심이고, 「출납에만」은 은행 자료가 온전한 날만 센다.
 */

type View = 'bank' | 'cash' | 'days' | 'pairs';
const HOW: Record<CashOutHow, string> = {
  same: '같은 날·같은 금액', fee: '수수료 붙음', near: '날짜 다름(±3일)', bundle: '여러 출납 줄 합', many: '여러 은행 건 합', daysum: '그날 합계',
};
const STATUS = { ok: ['자료 온전', 'text-emerald-700'], missing: ['출금 빠짐', 'text-amber-700'], nofile: ['잔액 파일 없음', 'text-ink-faint'] } as const;
const errMsg = (e: unknown) => (e instanceof Error ? e.message : String(e));
const thisMonth = () => new Date(Date.now() + 9 * 3600_000).toISOString().slice(0, 7);
const sum = (xs: { amount: string }[]) => xs.reduce((s, x) => s + Number(x.amount), 0);
const th = 'whitespace-nowrap bg-white px-2 py-1.5';
const td = 'whitespace-nowrap px-2 py-1';

export function CashOutMatchTab({ corps }: { corps: AcctCorp[] }) {
  const [mon, setMon] = useState(thisMonth());
  const [corpId, setCorpId] = useState<number | null>(null);
  const [data, setData] = useState<CashOutMatchResult | null>(null);
  const [view, setView] = useState<View>('bank');
  const [term, setTerm] = useState('');
  const [err, setErr] = useState('');

  const load = useCallback(async () => {
    setErr(''); setData(null);
    try { setData(await api.getCashOutMatch({ mon, corpId })); } catch (e) { setErr(errMsg(e)); }
  }, [mon, corpId]);
  useEffect(() => { void load(); }, [load]);

  const corpName = useMemo(() => new Map((data?.corps ?? []).map((c) => [c.corp_id, c.short_name])), [data]);
  const bankById = useMemo(() => new Map((data?.bank ?? []).map((b) => [b.id, b])), [data]);
  const cashById = useMemo(() => new Map((data?.cash ?? []).map((c) => [c.id, c])), [data]);
  const words = useMemo(() => searchWords(term.trim().toLowerCase()), [term]);

  const onlyBank = useMemo(() => (data?.onlyBank ?? []).map((id) => bankById.get(id)!)
    .filter((b) => hitAny(words, [b.summary, b.memo, b.bank, b.account_no, corpName.get(b.corp), b.amount])), [data, words]);
  const onlyCash = useMemo(() => (data?.onlyCash ?? []).map((id) => cashById.get(id)!)
    .filter((c) => hitAny(words, [c.method, c.cash_account, c.vendor, c.summary, c.corp_name, c.amount])), [data, words]);
  const days = data?.days ?? [];
  const okDays = days.filter((d) => d.status === 'ok').length;
  const matchedN = (data?.groups ?? []).reduce((s, g) => s + g.bank.length, 0);
  const unknownCash = (data?.cashUnknown ?? []).map((id) => cashById.get(id)!);

  return (
    <div className="space-y-2">
      <div className="flex flex-wrap items-center gap-1 text-sm">
        <input type="month" className="field w-36" aria-label="월" value={mon} onChange={(e) => e.target.value && setMon(e.target.value)} />
        <select className="field w-32" aria-label="법인" value={corpId ?? ''} onChange={(e) => setCorpId(e.target.value ? Number(e.target.value) : null)}>
          <option value="">전 법인</option>
          {corps.filter((c) => c.parent_corp_id == null).map((c) => <option key={c.corp_id} value={c.corp_id}>{c.short_name}</option>)}
        </select>
        {([['bank', `은행에만 ${data ? onlyBank.length : ''}`], ['cash', `출납에만 ${data ? onlyCash.length : ''}`], ['days', '날짜별'], ['pairs', `짝 맞음 ${data ? data.groups.length : ''}`]] as [View, string][]).map(([id, label]) => (
          <button key={id} onClick={() => setView(id)}
                  className={`rounded-lg px-2.5 py-1 ${view === id ? 'bg-brand-600 font-semibold text-white shadow-sm' : 'text-ink hover:bg-brand-50'}`}>{label}</button>
        ))}
        <input className="field ml-auto w-56" placeholder="적요·내용·업체·금액" value={term} onChange={(e) => setTerm(e.target.value)} />
      </div>
      {data && (
        <p className="page-hint px-1">
          {data.from}~{data.to} 은행 출금 {data.bank.length}건 {money(sum(data.bank))} 중 짝 맞음 {matchedN}건 · 은행에만 {data.onlyBank.length}건 {money(sum(data.onlyBank.map((id) => bankById.get(id)!)))}
          {data.transfer.length > 0 && <> · 계좌간 이체 {data.transfer.length}건</>}
          {' '}· 출납에만 {data.onlyCash.length}줄 {money(sum(data.onlyCash.map((id) => cashById.get(id)!)))}.
          {' '}<b>은행에만</b> = 출납일보에 없는 출금(먼저 볼 것). 현금 줄(시재계정 [현금]…)은 대조 밖입니다.
        </p>
      )}
      {data && okDays < days.length && (
        <div className="card border-amber-300 bg-amber-50 px-3 py-2 text-sm text-amber-900">
          ⚠ 은행 거래내역에 출금이 빠진 날이 {days.length - okDays}/{days.length}일입니다(일자별 잔액 파일의 출금과 안 맞음). 그날 출납 {unknownCash.length}줄 {money(sum(unknownCash))}은
          「출납에만」에 넣지 않았습니다. 인사이드뱅크 거래내역을 <b>입출금 전체</b>로 받아 「은행 파일 올리기」에 다시 올리면 채워집니다(이미 있는 줄은 두 번 안 들어갑니다).
        </div>
      )}
      {err && <ErrorBox message={err} />}
      <FitBox deps={[data, view, term, err]}>
        {view === 'bank' && (
          <table className="w-full text-sm">
            <thead className="sticky top-0">
              <tr className="text-left text-xs text-ink-soft">{['날짜', '법인', '은행', '계좌', '적요', '내용', '금액'].map((h) => <th key={h} className={th}>{h}</th>)}</tr>
            </thead>
            <tbody>
              {onlyBank.length === 0 && <tr><td colSpan={7} className="px-2 py-6 text-center text-ink-faint">{data ? '출납일보에 없는 출금이 없습니다.' : '불러오는 중…'}</td></tr>}
              {onlyBank.map((b) => (
                <tr key={b.id} className="border-t border-line">
                  <td className={td}>{b.date}</td>
                  <td className={td}>{dash(corpName.get(b.corp))}</td>
                  <td className={td}>{b.bank}</td>
                  <td className={`${td} font-mono text-xs`} title={b.alias ?? ''}>{b.account_no}</td>
                  <td className={td}>{dash(b.summary)}</td>
                  <td className="max-w-[18rem] truncate px-2 py-1" title={b.memo ?? ''}>{dash(b.memo)}</td>
                  <td className={`${td} text-right`}>{money(b.amount)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
        {view === 'cash' && (
          <table className="w-full text-sm">
            <thead className="sticky top-0">
              <tr className="text-left text-xs text-ink-soft">{['지급일', '법인', '구분', '시재계정', '업체', '적요', '금액'].map((h) => <th key={h} className={th}>{h}</th>)}</tr>
            </thead>
            <tbody>
              {onlyCash.length === 0 && <tr><td colSpan={7} className="px-2 py-6 text-center text-ink-faint">{data ? '은행 자료가 온전한 날 중 짝 없는 출납 줄이 없습니다.' : '불러오는 중…'}</td></tr>}
              {onlyCash.map((c) => (
                <tr key={c.id} className="border-t border-line">
                  <td className={td}>{c.date}</td>
                  <td className={td}>{c.corp_name}</td>
                  <td className={td}>{dash(c.method)}</td>
                  <td className={td}>{dash(c.cash_account)}</td>
                  <td className={td}>{dash(c.vendor)}</td>
                  <td className="max-w-[18rem] truncate px-2 py-1" title={c.summary ?? ''}>{dash(c.summary)}</td>
                  <td className={`${td} text-right`}>{money(c.amount)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
        {view === 'days' && (
          <table className="w-full text-sm">
            <thead className="sticky top-0">
              <tr className="text-left text-xs text-ink-soft">
                {['날짜', '법인', '은행 자료', '잔액 파일 출금', '거래내역 출금', '출납', '짝 맞음', '은행에만', '출납에만', '자료 빠진 날 출납'].map((h) => <th key={h} className={th}>{h}</th>)}
              </tr>
            </thead>
            <tbody>
              {days.length === 0 && <tr><td colSpan={10} className="px-2 py-6 text-center text-ink-faint">{data ? '이 달 출금이 없습니다.' : '불러오는 중…'}</td></tr>}
              {days.map((d) => (
                <tr key={`${d.corp}|${d.date}`} className="border-t border-line">
                  <td className={td}>{d.date}</td>
                  <td className={td}>{dash(corpName.get(d.corp))}</td>
                  <td className={`${td} text-xs ${STATUS[d.status][1]}`}>{STATUS[d.status][0]}</td>
                  <td className={`${td} text-right`}>{money(d.bal_out)}</td>
                  <td className={`${td} text-right`}>{money(d.bank_out)}</td>
                  <td className={`${td} text-right`}>{money(d.cash_out)}</td>
                  <td className={`${td} text-right`}>{money(d.matched)}</td>
                  <td className={`${td} text-right ${Number(d.only_bank) ? 'font-semibold text-red-700' : ''}`}>{Number(d.only_bank) ? `${money(d.only_bank)} (${d.only_bank_n})` : '—'}</td>
                  <td className={`${td} text-right ${Number(d.only_cash) ? 'text-amber-800' : ''}`}>{Number(d.only_cash) ? `${money(d.only_cash)} (${d.only_cash_n})` : '—'}</td>
                  <td className={`${td} text-right text-ink-faint`}>{Number(d.unknown_cash) ? money(d.unknown_cash) : '—'}</td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
        {view === 'pairs' && (
          <table className="w-full text-sm">
            <thead className="sticky top-0">
              <tr className="text-left text-xs text-ink-soft">{['방법', '은행 출금', '출납일보'].map((h) => <th key={h} className={th}>{h}</th>)}</tr>
            </thead>
            <tbody>
              {(data?.groups ?? []).length === 0 && <tr><td colSpan={3} className="px-2 py-6 text-center text-ink-faint">{data ? '짝 맞은 출금이 없습니다.' : '불러오는 중…'}</td></tr>}
              {(data?.groups ?? []).map((g, i) => (
                <tr key={i} className="border-t border-line align-top">
                  <td className={`${td} text-xs`}>{HOW[g.how]}</td>
                  <td className="px-2 py-1 text-xs">
                    {g.bank.map((id) => { const b = bankById.get(id)!; return <div key={id}>{b.date} {corpName.get(b.corp)} {b.summary} <b>{money(b.amount)}</b></div>; })}
                  </td>
                  <td className="px-2 py-1 text-xs">
                    {g.cash.map((id) => { const c = cashById.get(id)!; return <div key={id}>{c.date} {c.corp_name} {dash(c.cash_account)} · {dash(c.vendor)} <b>{money(c.amount)}</b></div>; })}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </FitBox>
    </div>
  );
}
